import { make, stopPropagationOnEditorJsControlKeys } from '@/editorjs/utils';

export default class VoteTool {
  static get toolbox() {
    return {
      title: "Vote",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5h-2a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-12a2 2 0 0 0 -2 -2h-2" /><path d="M9 3m0 2a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v0a2 2 0 0 1 -2 2h-2a2 2 0 0 1 -2 -2z" /><path d="M9 17v-5" /><path d="M12 17v-1" /><path d="M15 17v-3" /></svg>`
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get enableLineBreaks() {
    return false;
  }

  static get MIN_OPTIONS() {
    return 2;
  }

  static get MAX_OPTIONS() {
    return 10;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.config = config;
    this.readOnly = readOnly;
    this.block = block;

    this.isSaving = false;
    this.saveTimeout = null;
    this.isLoading = false;

    // Feature restriction
    this.canUseVote = config.canUseVote !== false; // default true for backward compatibility
    this.restrictionMessage = config.restrictionMessage || '';
    this.restrictionButtonText = config.restrictionButtonText || '';
    this.restrictionButtonUrl = config.restrictionButtonUrl;

    // 삭제된 옵션 ID 추적 (Rails nested_attributes _destroy 처리용)
    this._deletedOptionIds = [];

    this.nodes = {
      wrapper: null,
      label: null,
      questionInput: null,
      optionsList: null,
      addOptionButton: null,
      loadingIndicator: null,
    };

    const _CSS = {
      baseClass: this.api.styles.block,
      wrapper: "vote-tool-wrapper",
      label: "vote-tool__label",
      questionInput: "vote-tool__question-input",
      optionsList: "vote-tool__options-list",
      optionItem: "vote-tool__option-item",
      optionInput: "vote-tool__option-input",
      optionDelete: "vote-tool__option-delete",
      addOptionButton: "vote-tool__add-option",
      placeholder: "vote-tool__placeholder",
      loading: "vote-tool__loading",
    };

    this.CSS = Object.assign(_CSS, config.css || {});

    // editor_data에 저장되는 데이터 (vote_id + question + options).
    // collab 시스템이 이 데이터를 Y.Doc을 통해 다른 사용자에게 전파한다.
    this._data = {
      vote_id: data.vote_id || null,
    };

    // 입력 중 상태. 최초엔 data.question/options로부터 흡수, 이후 input 이벤트로 갱신.
    this._workingData = {
      question: typeof data.question === 'string' ? data.question : '',
      options: Array.isArray(data.options) && data.options.length > 0
        ? data.options.map((o, i) => ({
            id: o.id,
            text: o.text || '',
            position: typeof o.position === 'number' ? o.position : i,
          }))
        : []
    };
  }

  get data() {
    return this._data;
  }

  // blocks.update() fallback 경로 — 원격 변경을 인스턴스에 흡수하고 DOM sync.
  set data(data) {
    this._data = {
      vote_id: data.vote_id || null,
    };
    if (typeof data.question === 'string') {
      this._workingData.question = data.question;
    }
    if (Array.isArray(data.options) && data.options.length > 0) {
      this._workingData.options = data.options.map((o, i) => ({
        id: o.id,
        text: o.text || '',
        position: typeof o.position === 'number' ? o.position : i,
      }));
    }
    if (this.nodes.questionInput) {
      this._syncDomFromWorkingData();
    }
  }

  _syncDomFromWorkingData() {
    if (this.nodes.questionInput && this.nodes.questionInput.textContent !== this._workingData.question) {
      this.nodes.questionInput.textContent = this._workingData.question;
    }
    if (!this.nodes.optionsList) return;
    const currentEls = this.nodes.optionsList.querySelectorAll(`.${this.CSS.optionInput}`);
    if (currentEls.length !== this._workingData.options.length) {
      this.renderOptions();
      return;
    }
    this._workingData.options.forEach((opt, idx) => {
      const el = this.nodes.optionsList.querySelector(`[data-index="${idx}"]`);
      if (el && el.textContent !== (opt.text || '')) {
        el.textContent = opt.text || '';
      }
    });
  }

  // EditorJS 생명주기 validate — collab 환경에서 항상 true를 반환해야 한다.
  // false면 editor.save() 결과에서 블록이 제외 → YEditorBinding이 Y.Doc에서 삭제로 해석 →
  // 다른 사용자에게 블록이 사라지는 증상 발생. 입력 중 빈 필드가 있어도 블록 자체는
  // editor_data에 유지되어야 하며, 백엔드 저장(vote 모델 생성/수정) 여부는 _isBackendSaveable()로 별도 판단.
  validate(savedData) {
    return true;
  }

  // 백엔드(Vote 모델)에 저장 가능한 상태인지 — saveToBackend 내부에서만 사용.
  _isBackendSaveable() {
    if (!this._workingData.question || this._workingData.question.trim() === "") {
      return false;
    }
    if (!this._workingData.options || this._workingData.options.length < VoteTool.MIN_OPTIONS) {
      return false;
    }
    if (this._workingData.options.length > VoteTool.MAX_OPTIONS) {
      return false;
    }
    for (const option of this._workingData.options) {
      if (!option.text || option.text.trim() === "") {
        return false;
      }
    }
    return true;
  }

  save(block) {
    // collab 동기화용 — DOM을 진실로 삼아 전체 페이로드를 editor_data에 넣는다.
    // Yjs가 이 값을 다른 사용자에게 전파하고, _tryPatchBlock('vote')가 수신 측 DOM을 갱신.
    const questionText = this.nodes.questionInput?.textContent ?? this._workingData.question ?? '';
    const optionEls = this.nodes.optionsList
      ? Array.from(this.nodes.optionsList.querySelectorAll(`.${this.CSS.optionInput}`))
      : [];

    const options = optionEls.length
      ? optionEls.map((el, idx) => ({
          id: this._workingData.options[idx]?.id,
          text: el.textContent ?? '',
          position: idx,
        }))
      : this._workingData.options.map((o, idx) => ({
          id: o.id,
          text: o.text || '',
          position: idx,
        }));

    return {
      vote_id: this._data.vote_id || null,
      question: questionText,
      options,
    };
  }

  static get sanitize() {
    return {
      vote_id: false,
      question: {},
      options: {
        id: false,
        text: {},
        position: false,
      },
    };
  }

  render() {
    this.nodes.wrapper = make('div', [this.CSS.baseClass, this.CSS.wrapper]);

    // Check feature restriction first
    if (!this.canUseVote) {
      this.renderRestriction();
      return this.nodes.wrapper;
    }

    // editor_data(= Y.Doc)에 이미 question/options가 있으면 즉시 렌더 — network fetch 불필요.
    // 레거시 문서(vote_id만 있음)는 여전히 fetch로 복원.
    const hasLocalData =
      (this._workingData.question && this._workingData.question.length > 0) ||
      (Array.isArray(this._workingData.options) && this._workingData.options.length > 0);

    if (hasLocalData) {
      this.renderContent();
    } else if (this._data.vote_id) {
      this.showLoading();
      this.fetchVoteData().then(() => {
        this.hideLoading();
        this.renderContent();
      });
    } else {
      // 새 투표 — 초기 옵션 2개
      this._workingData.options = [
        { text: '', position: 0 },
        { text: '', position: 1 }
      ];
      this.renderContent();
    }

    return this.nodes.wrapper;
  }

  showLoading() {
    this.isLoading = true;
    this.nodes.loadingIndicator = make('div', [this.CSS.loading]);
    this.nodes.loadingIndicator.textContent = '로딩 중...';
    this.nodes.wrapper.appendChild(this.nodes.loadingIndicator);
  }

  hideLoading() {
    this.isLoading = false;
    if (this.nodes.loadingIndicator) {
      this.nodes.loadingIndicator.remove();
      this.nodes.loadingIndicator = null;
    }
  }

  renderRestriction() {
    const restrictionContainer = make('div', ['p-6', 'text-center']);

    const icon = make('div', ['text-4xl', 'mb-3']);
    icon.textContent = '🔒';

    restrictionContainer.appendChild(icon);

    if (this.restrictionMessage) {
      const message = make('p', ['text-base', 'font-medium', 'text-gray-700', 'dark:text-gray-300', 'mb-4']);
      message.textContent = this.restrictionMessage;
      restrictionContainer.appendChild(message);
    }

    if (this.restrictionButtonUrl && this.restrictionButtonText) {
      const button = make('a', ['inline-block', 'px-4', 'py-2', 'bg-green-333', 'hover:bg-green-700', 'text-white', 'font-medium', 'rounded-lg', 'transition-colors', 'no-underline']);
      button.href = this.restrictionButtonUrl;
      button.target = '_blank';
      button.textContent = this.restrictionButtonText;
      restrictionContainer.appendChild(button);
    }

    this.nodes.wrapper.appendChild(restrictionContainer);
  }

  async fetchVoteData() {
    try {
      const url = `${this.config.endpoint}/${this._data.vote_id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        }
      });

      const data = await response.json();

      if (data.success) {
        this._workingData.question = data.vote.question;
        this._workingData.options = data.vote.options.map(opt => ({
          id: opt.id,
          text: opt.text,
          position: opt.position
        }));
      } else {
        throw new Error(data.errors?.join(', ') || '데이터를 불러오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('Vote fetch error:', error);
      this.api.notifier.show({
        message: error.message || '데이터를 불러오는 중 오류가 발생했습니다.',
        style: 'error'
      });

      // 에러 시 빈 데이터로 초기화
      this._workingData.question = '';
      this._workingData.options = [
        { text: '', position: 0 },
        { text: '', position: 1 }
      ];
    }
  }

  renderContent() {
    // 투표 라벨
    this.nodes.label = make('div', [this.CSS.label]);
    this.nodes.label.textContent = '투표';
    this.nodes.wrapper.appendChild(this.nodes.label);

    // 질문 입력
    this.nodes.questionInput = make('div', [this.CSS.questionInput], {
      contentEditable: !this.readOnly,
    });
    this.nodes.questionInput.dataset.placeholder = '투표 질문을 입력하세요';
    this.nodes.questionInput.textContent = this._workingData.question;

    this.nodes.questionInput.addEventListener('input', this.onQuestionInput);
    this.nodes.questionInput.addEventListener('keydown', this.onKeyDown);
    this.nodes.questionInput.addEventListener('blur', this.onQuestionBlur);

    this.nodes.wrapper.appendChild(this.nodes.questionInput);

    // 옵션 리스트
    this.nodes.optionsList = make('div', [this.CSS.optionsList]);
    this.renderOptions();
    this.nodes.wrapper.appendChild(this.nodes.optionsList);

    // 옵션 추가 버튼
    if (!this.readOnly) {
      this.nodes.addOptionButton = make('button', [this.CSS.addOptionButton], {
        type: 'button'
      });
      this.nodes.addOptionButton.textContent = '+ 옵션 추가';
      this.nodes.addOptionButton.addEventListener('click', this.addOption);
      this.nodes.wrapper.appendChild(this.nodes.addOptionButton);
    }
  }

  renderOptions = () => {
    this.nodes.optionsList.innerHTML = '';

    this._workingData.options.forEach((option, index) => {
      const optionItem = this.createOptionItem(option, index);
      this.nodes.optionsList.appendChild(optionItem);
    });
  }

  createOptionItem = (option, index) => {
    const item = make('div', [this.CSS.optionItem, 'group']);

    const input = make('div', [this.CSS.optionInput], {
      contentEditable: !this.readOnly,
    });
    input.dataset.placeholder = `옵션 ${index + 1}`;
    input.dataset.index = index;
    input.textContent = option.text || '';

    input.addEventListener('input', this.onOptionInput);
    input.addEventListener('keydown', this.onKeyDown);
    input.addEventListener('blur', () => this.scheduleAutoSave());

    item.appendChild(input);

    if (!this.readOnly) {
      const deleteBtn = make('button', [this.CSS.optionDelete], {
        type: 'button'
      });
      deleteBtn.innerHTML = '×';
      deleteBtn.dataset.index = index;
      deleteBtn.addEventListener('click', this.deleteOption);

      // 최소 2개는 유지
      if (this._workingData.options.length <= VoteTool.MIN_OPTIONS) {
        deleteBtn.disabled = true;
      }

      item.appendChild(deleteBtn);
    }

    return item;
  }

  onQuestionInput = (event) => {
    const text = event.target.textContent;
    this._workingData.question = text;

    // 내용이 비어있을 때 DOM 정리하여 커서 위치 문제 해결
    if (text.trim() === '') {
      event.target.innerHTML = '';
    }

    this.scheduleAutoSave();
    this.block?.dispatchChange();
  }

  onQuestionBlur = () => {
    this.scheduleAutoSave();
    this.block?.dispatchChange();
  }

  onOptionInput = (event) => {
    const index = parseInt(event.target.dataset.index);
    const text = event.target.textContent;
    this._workingData.options[index].text = text;
    this._workingData.options[index].position = index;

    // 내용이 비어있을 때 DOM 정리하여 커서 위치 문제 해결
    if (text.trim() === '') {
      event.target.innerHTML = '';
    }

    this.scheduleAutoSave();
    this.block?.dispatchChange();
  }

  onKeyDown = (event) => {
    stopPropagationOnEditorJsControlKeys(event);

    // Enter 키 처리
    if (event.key === 'Enter') {
      event.preventDefault();

      // 질문 입력에서 Enter를 누르면 첫 번째 옵션으로 포커스
      if (event.target === this.nodes.questionInput) {
        const firstOption = this.nodes.optionsList.querySelector(`.${this.CSS.optionInput}`);
        if (firstOption) {
          firstOption.focus();
        }
      }
      // 옵션 입력에서 Enter를 누르면 다음 옵션으로 포커스 (마지막이면 새 옵션 추가)
      else if (event.target.classList.contains(this.CSS.optionInput)) {
        const index = parseInt(event.target.dataset.index);
        const nextIndex = index + 1;

        if (nextIndex < this._workingData.options.length) {
          const nextOption = this.nodes.optionsList.querySelector(`[data-index="${nextIndex}"]`);
          if (nextOption) {
            nextOption.focus();
          }
        } else if (this._workingData.options.length < VoteTool.MAX_OPTIONS) {
          this.addOption(event);
        }
      }
    }
  }

  addOption = (event) => {
    if (event) {
      event.preventDefault();
    }

    if (this._workingData.options.length >= VoteTool.MAX_OPTIONS) {
      this.api.notifier.show({
        message: `최대 ${VoteTool.MAX_OPTIONS}개까지 옵션을 추가할 수 있습니다.`,
        style: 'error'
      });
      return;
    }

    this._workingData.options.push({
      text: '',
      position: this._workingData.options.length
    });

    this.renderOptions();
    this.scheduleAutoSave();
    this.block?.dispatchChange();

    // 새로 추가된 옵션에 포커스
    setTimeout(() => {
      const lastOption = this.nodes.optionsList.querySelector(`[data-index="${this._workingData.options.length - 1}"]`);
      if (lastOption) {
        lastOption.focus();
      }
    }, 0);
  }

  deleteOption = (event) => {
    event.preventDefault();
    const index = parseInt(event.target.dataset.index);

    if (this._workingData.options.length <= VoteTool.MIN_OPTIONS) {
      this.api.notifier.show({
        message: `최소 ${VoteTool.MIN_OPTIONS}개의 옵션이 필요합니다.`,
        style: 'error'
      });
      return;
    }

    // 삭제할 옵션 가져오기
    const deletedOption = this._workingData.options[index];

    // 기존 옵션인 경우 (id가 있으면) 삭제 목록에 추가
    if (deletedOption.id) {
      this._deletedOptionIds.push(deletedOption.id);
    }

    this._workingData.options.splice(index, 1);

    // 포지션 재정렬
    this._workingData.options.forEach((option, idx) => {
      option.position = idx;
    });

    this.renderOptions();
    this.scheduleAutoSave();
    this.block?.dispatchChange();
  }

  scheduleAutoSave = () => {
    if (this.readOnly) return;

    // 기존 타이머 취소
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }

    // 500ms 후 자동 저장
    this.saveTimeout = setTimeout(() => {
      this.saveToBackend();
    }, 500);
  }

  async saveToBackend() {
    if (this.isSaving || this.readOnly) return;

    // 데모에서는 실제 Vote 모델을 저장(vote_id 발급)하지 않고 툴 동작만 시연한다.
    if (this.config.isDemo) return;

    // 백엔드 Vote 모델 저장은 질문+옵션 모두 채워져야만 — 불완전한 상태의 서버 왕복을 막는다.
    // (editor_data Y.Doc 동기화는 validate와 무관하게 항상 발생 — 이게 핵심)
    if (!this._isBackendSaveable()) {
      return;
    }

    this.isSaving = true;

    try {
      const method = this._data.vote_id ? 'PUT' : 'POST';
      const url = this._data.vote_id
        ? `${this.config.endpoint}/${this._data.vote_id}`
        : this.config.endpoint;

      const payload = {
        vote: {
          question: this._workingData.question,
          vote_options_attributes: [
            // 현재 옵션들
            ...this._workingData.options.map((option, index) => {
              const attr = {
                text: option.text,
                position: index,
                _destroy: false
              };
              // 기존 옵션인 경우에만 id 포함
              if (option.id) {
                attr.id = option.id;
              }
              return attr;
            }),
            // 삭제된 옵션들 (_destroy: true로 전송)
            ...this._deletedOptionIds.map(id => ({
              id: id,
              _destroy: true
            }))
          ]
        }
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        // vote_id가 처음 생긴 시점인지 기록 — 그럴 때만 Y.Doc에 dispatchChange로 전파.
        const wasNewVote = !this._data.vote_id;
        this._data.vote_id = data.vote.id;

        // POST in-flight 중 원격에서 온 변경이 있었을 수 있음 — 서버가 발급한 id만 merge하고
        // text/position은 로컬(_workingData) 우선. 서버 응답이 로컬 입력을 덮어쓰지 않도록.
        const incomingByPos = new Map((data.vote.options || []).map(o => [o.position, o]));
        this._workingData.options = this._workingData.options.map((o, idx) => ({
          ...o,
          id: o.id || incomingByPos.get(idx)?.id,
        }));

        // 삭제 목록 초기화 (저장 완료 후)
        this._deletedOptionIds = [];

        if (wasNewVote) {
          this.block?.dispatchChange();
        }
      } else {
        throw new Error(data.errors?.join(', ') || '저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Vote save error:', error);
      this.api.notifier.show({
        message: error.message || '저장 중 오류가 발생했습니다.',
        style: 'error'
      });
    } finally {
      this.isSaving = false;
    }
  }

  destroy() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  }

  get shortcut() {
    return 'CMD+SHIFT+V';
  }
}
